/** Beat 11's only action: crossing the office and leaving through its ajar door. */

import type { Beat, Stage, StoryContext } from '@/story/types';

export const OFFICE_FLAGS = { leftOffice: 'office.leftOffice' } as const;

/**
 * The two cues the close needs, and why beat 11 has a `close` stage at all.
 *
 * It shipped without one. Every sibling — `twelveOut.ts`, `woolShedRest.ts`,
 * `woolShedPuzzle.ts`, `blackoutHall.ts`, `blackoutReturn.ts` — ends on a stage
 * that emits a fade, and beat 11 ended by flipping `finished` and cutting. Beat
 * 11 has no `next`, so that cut is straight into the credits, which is the
 * hardest edit in the game landing on the quietest beat in it. Found on
 * independent review of stage 1.
 *
 * `open` is emitted on entry because `FadeOverlay` starts fully black and that
 * is deliberate (`docs/traps.md`) — a beat that does not open on black has to
 * say so, and this one opens on a room with a lamp in it.
 */
export const OFFICE_CUES = {
  open: 'office.open',
  fadeOut: 'office.fadeOut',
} as const;

/**
 * How long the black is held after she is through the door.
 *
 * Beat 10's `CLOSE_HOLD` is the same idea and this is deliberately a little
 * shorter: she walks out of a small room into a lit corridor, where beat 10's
 * close is a fade over a pipe she has gone into. There is nothing to look at
 * here once she is past the leaf.
 */
const CLOSE_HOLD = 2.4;

/**
 * The backstop, and D-097 is why it exists rather than a bare condition.
 *
 * A player who never crosses the plane — who stands and looks, which this beat
 * explicitly permits and is partly about — must still reach the end. It is not
 * a fail state (spec §5.6): it ends the stage and nothing about the room
 * changes when it fires. Generous, because *nothing is required* in this beat
 * and being hurried is the one thing it must not do.
 */
const CROSSING_PATIENCE = 300;

function crossing(): Stage {
  return {
    id: 'crossing',
    enter: (_ctx, emit): void => emit(OFFICE_CUES.open),
    done: (ctx: StoryContext): boolean => ctx.completed.has(OFFICE_FLAGS.leftOffice) || ctx.stageTime >= CROSSING_PATIENCE,
  };
}

export const OFFICE_BEAT: Beat = {
  number: 11,
  id: 'act3.office',
  stages: [
    crossing(),
    {
      id: 'close',
      enter: (_ctx, emit): void => emit(OFFICE_CUES.fadeOut),
      done: (ctx: StoryContext): boolean => ctx.stageTime >= CLOSE_HOLD,
    },
  ],
};
